import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { Fragment, useState, Children, cloneElement, useEffect } from "react";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { twMerge } from "tailwind-merge";
import { messageFilter, SafeFormattedMessage } from "../login/SafeFormattedMessage";

let displayed = [];

export const LoginFormExternalError = ({ reportError, clearError, error, errorName, show }) => {
    useEffect(() => {
        errorName ??= error;
        if (show && error) {
            reportError(errorName, error);
        } else if (!show || !error) {
            clearError(errorName || error)
        }
        return () => {
            clearError(errorName || error)
        }
    }, [error, show, errorName]);
    return null;
}
LoginFormExternalError.propTypes = {
    show: PropTypes.bool.isRequired,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    errorName: PropTypes.string,
    reportError: PropTypes.func,
    clearError: PropTypes.func,
}

export const MfaPageContainer = ({ onSubmit = () => { }, onChange, children, submitButton, submitButtonText, secondaryButtonText, secondaryButtonHandler, resendButtonText, resendButtonHandler, headerText, instructionalText, footNoteText, otpValidator, showErrors = true }) => {
    const [externalErrors, setExternalErrors] = useState({});
    const [allowSubmit, setAllowSubmit] = useState(typeof otpValidator === "function" ? false : true);
    const translatableMessages = useSelector(state => state.loginForm.translatableMessages ?? []);
    const loginErrorMessages = useSelector(state => state.loginForm.loginErrorMessages ?? []);
    const intl = useIntl();

    const storeDisplayed = (key) => {
        displayed = [...displayed, key];
    };

    const removeDisplayed = (id) => {
        displayed = [...displayed.filter(key => id !== key)];
    };

    const showSnackbar = (message) => {
        enqueueSnackbar(message, {
            variant: "success",
            onExited: (event, myKey) => {
                removeDisplayed(myKey);
            }
        });
    };

    // Handle success messages
    useEffect(() => {
        translatableMessages
            .filter((item)=>item?.options?.variant === "success")
            .filter(messageFilter)
            .forEach(({ key, message, context = {} }) => {
                // do nothing if snackbar is already displayed
                if (displayed.includes(key)) return;

                const successMessage = intl.formatMessage(message, context);
                showSnackbar(successMessage);
                storeDisplayed(key);
            });
        
    }, [translatableMessages]);

    // Handle external errors from Redux state 
    useEffect(() => {
        setExternalErrors({});
        loginErrorMessages?.forEach(({ key, message }) => {
            setExternalErrors(prev => ({
                ...prev,
                [key]: message,
            }));
        });

        translatableMessages.filter((item) => Boolean(item.message))
            .forEach(({ key, message, context = {}, options = {} }) => {
                // do nothing if snackbar is already displayed
                if (displayed.includes(key)) return;

                if (options.variant === "error") {
                    const errorMessage = message ? <SafeFormattedMessage {...message} values={context} /> : null;
                    setExternalErrors(prev => ({
                        ...prev,
                        [key]: errorMessage,
                    }));
                    storeDisplayed(key);
                }
            });
    }, [loginErrorMessages, translatableMessages]);

    const RenderErrors = ({ show }) => {
        const externalErrorMessages = Object.values(externalErrors).filter(Boolean).flat();
        const display = show && externalErrorMessages.length > 0;
        return (
            <div
                className={twMerge("bg-error-100 w-full border border-error-100 text-error-700 text-base pr-5 py-4 rounded mb-4", !display && "hidden")}
                role="alert"
                aria-live={display ? "assertive" : "off"}
            >
                <ul className="list-none pl-5 flex flex-col space-y-1">
                    {externalErrorMessages.map((errMsg, index) => (
                        <li key={index}>{errMsg}</li>
                    ))}
                </ul>
            </div>
        );
    };
    RenderErrors.propTypes = {
        show: PropTypes.bool,
    }

    const handleInputChange = (value) => {
        if (typeof onChange === "function") {
            onChange(value);
        }
        if (typeof otpValidator === "function") {
            setAllowSubmit(otpValidator(value));
        }
    };

    const onSubmitHandler = (e) => {
        if (allowSubmit) {
            onSubmit(e);
            setExternalErrors({});
        }
    };

    const buttonDisabled = !allowSubmit;
    const bespokeSubmitButtonClassname = "w-full bg-accent-600 hover:bg-blue-700 text-white font-medium py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg";
    const bespokeSecondaryButtonClassname = "w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg transition-colors duration-200";
    const bespokeResendButtonClassname = "cursor-pointer text-Button-text text-sm font-medium underline";
    return (
        <Fragment>
            <h2 id="mfaPageTitle" className="text-2xl text-primary-fg mb-6">
                {headerText}
            </h2>
            <RenderErrors show={showErrors} />
            <div className="space-y-3 pb-5 flex flex-col">
                {instructionalText}
            </div>
            <form className="space-y-5">
                <div className="space-y-4">
                    {Children.map(children, child => {
                        if (!React.isValidElement(child)) {
                            return null;
                        }
                        if (child?.props?.id === undefined) {
                            return child;
                        }
                        return cloneElement(child, { onChange: handleInputChange })
                    })}
                </div>
                {submitButton ??
                    <button
                        id="mfaContinueButton"
                        disabled={buttonDisabled}
                        type="submit"
                        className={twMerge(bespokeSubmitButtonClassname, buttonDisabled && "hover:bg-neutral-500 bg-neutral-400 cursor-not-allowed")}
                        onClick={onSubmitHandler}
                    >
                        {submitButtonText ?? <FormattedMessage id="mfaSetup.continueButton" defaultMessage="Continue" description="A continue button" />}
                    </button>
                }
                {secondaryButtonText && secondaryButtonHandler &&
                    <button
                        id="mfaBackButton"
                        type="button"
                        className={bespokeSecondaryButtonClassname}
                        onClick={secondaryButtonHandler}
                    >
                        {secondaryButtonText}
                    </button>
                }
                {resendButtonText && resendButtonHandler &&
                    <div className="py-4 flex flex-col justify-center items-center">
                        <div id="mfaResendCode" className={bespokeResendButtonClassname} onClick={resendButtonHandler} tabIndex="0">{resendButtonText}</div>
                    </div>
                }
            </form>
            {footNoteText &&
                <div id="mfaFootnote" className="w-full text-sm text-gray-700 mt-4 border border-gray-300 rounded py-2 px-4">
                    {footNoteText}
                </div>
            }
        </Fragment>
    );
}

MfaPageContainer.propTypes = {
    headerText: PropTypes.node,
    instructionalText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    footNoteText: PropTypes.node,
    submitButton: PropTypes.node,
    submitButtonText: PropTypes.node,
    secondaryButtonText: PropTypes.node,
    secondaryButtonHandler: PropTypes.func,
    resendButtonText: PropTypes.node,
    resendButtonHandler: PropTypes.func,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    onSubmit: PropTypes.func,
    onChange: PropTypes.func,
    otpValidator: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    showErrors: PropTypes.bool,
}